import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { IAbstractRecord } from '@models';
import { isEmpty, isValidNumber } from '@utils/validationUtils';
import { DynamicFormWidgetInputType, FormFieldType } from './types/inputTypes';
import DateService from '@services/DateService';
import { IDynamicFormWidgetSection } from './types';
import { cloneDeep } from 'lodash';

export function normalizeDynamicFormDrawerValues(
  sections: IDynamicFormWidgetSection[],
  values: IAbstractRecord
): [IAbstractRecord] {
  const outputValues: IAbstractRecord = cloneDeep(values);

  sections.forEach((section) => {
    Object.values(section.inputs).forEach((property) => {
      switch (property.type) {
        case FormFieldType.text:
          outputValues[property.name] = outputValues[property.name]
            ? outputValues[property.name].trim()
            : '';
          break;
        default:
          break;
      }
    });
  });

  return [outputValues];
}

export const validateDynamicFormWidget = (
  value: any,
  input: DynamicFormWidgetInputType,
  values: IAbstractRecord
): string | Record<string, string>[] => {
  if (input === undefined) {
    return '';
  }

  if (
    input.hidden ||
    (input.conditionalHidden && input.conditionalHidden(values))
  ) {
    return '';
  }

  if (
    (input.required ||
      (input.conditionalRequired && input.conditionalRequired(values))) &&
    isEmpty(value)
  ) {
    return 'Required';
  }

  if (
    value &&
    !(
      input.disabled ||
      (input.conditionalDisable && input.conditionalDisable(values))
    )
  ) {
    switch (input.type) {
      case FormFieldType.text: {
        if (
          input.minCharacters &&
          input.minCharacters > 0 &&
          value.length < input.minCharacters
        ) {
          return 'Min ' + input.minCharacters + ' characters';
        } else if (
          input.maxCharacters &&
          input.maxCharacters > 0 &&
          value.length > input.maxCharacters
        ) {
          return 'Max ' + input.maxCharacters + ' characters';
        }
        break;
      }

      case FormFieldType.number: {
        if (isValidNumber(input.minValue) && value < input.minValue) {
          return `Please enter a number higher than ${input.minValue}`;
        } else if (isValidNumber(input.maxValue) && value > input.maxValue) {
          return `Please enter a number lower than ${input.maxValue}`;
        }
        break;
      }

      case FormFieldType.phoneNumber: {
        value = value.replace('+', '');
        if (!isPossiblePhoneNumber(`+${value}`)) {
          return 'Invalid phone number';
        }
        break;
      }

      case FormFieldType.date: {
        if (!DateService.isValidDate(value)) {
          return 'Invalid Date';
        }
        break;
      }

      case FormFieldType.repeaterBox: {
        const result: Record<string, string>[] = [];

        value.forEach((item: Record<string, string>, index: number) => {
          result.push({});
          Object.keys(input.inputs).forEach((inputName) => {
            const inputError = validateDynamicFormWidget(
              item[inputName],
              input.inputs[inputName],
              values
            );
            if (typeof inputError === 'string') {
              result[index][inputName] = inputError;
            }
          });
        });
        return result;
      }
    }
    if (input.customValidator) {
      return input.customValidator(values);
    }
  }

  return '';
};
